/** Google Ads › Changes: review & publish change sets, revert, audit log (blueprint §9.2, prototype #changes). */
import { useState } from "react";
import { Link, useSearchParams } from "react-router";
import { Icon } from "../components/Icon";
import { Chip, Empty, ErrorBox, Loading, PageHeader, StatePill } from "../components/ui";
import { api, useApi, useEvents } from "../lib/api";
import { useSession } from "../lib/session";

interface SetRow {
  id: string;
  title: string;
  status: string;
  source_kind: string;
  simulated: boolean;
  created_at: string;
  apply_at: string | null;
  created_by: string | null;
  approved_by: string | null;
  reverts_id: string | null;
  ops?: number;
}
interface OpRow {
  id: string;
  resource_type: string;
  action: string;
  entity: Record<string, string>;
  fields: Record<string, unknown>;
  update_mask: string[] | null;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  label: string;
  reason: string | null;
  guardrail: { verdict: string; checks: Array<{ key: string; verdict: string; reason: string }> } | null;
  google_error: { message: string } | null;
  status: string;
}
interface Detail {
  set: SetRow;
  ops: OpRow[];
  campaignNames: Record<string, string>;
  people: Record<string, string>;
}
interface AuditRow {
  id: string;
  at: string;
  actor_kind: string;
  label: string | null;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  simulated: boolean;
  change_set_id: string | null;
}

function fmt(field: string, v: unknown): string {
  if (v === null || v === undefined) return "—";
  if (field.endsWith("_micros")) return `$${(Number(v) / 1e6).toFixed(2)}`;
  if (field === "target_roas") return `${v}%`;
  if (typeof v === "boolean") return v ? "yes" : "no";
  if (
    typeof v === "string" &&
    (field === "headlines" || field === "descriptions" || field === "final_urls")
  ) {
    try {
      const items = JSON.parse(v) as Array<string | { text: string; pinned_field?: string | null }>;
      return items
        .map((i) => (typeof i === "string" ? i : i.pinned_field ? `${i.text} [${i.pinned_field}]` : i.text))
        .join(" | ");
    } catch {
      return v;
    }
  }
  return String(v);
}
const when = (iso: string) =>
  new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });

export function Changes() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") === "audit" ? "audit" : "sets";
  const setTab = (t: string) =>
    setParams((p) => {
      p.set("tab", t);
      return p;
    });
  return (
    <div className="page fill">
      <PageHeader
        title="Changes"
        sub="Every write to Google Ads is a change set: drafted, validated by Google, checked by guardrails, approved, applied and revertible."
      >
        <div className="seg" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={tab === "sets"}
            className={tab === "sets" ? "on" : ""}
            onClick={() => setTab("sets")}
          >
            Change sets
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === "audit"}
            className={tab === "audit" ? "on" : ""}
            onClick={() => setTab("audit")}
          >
            Audit log
          </button>
        </div>
      </PageHeader>
      {tab === "audit" ? <AuditLog /> : <ChangeSets />}
    </div>
  );
}

function ChangeSets() {
  const [params, setParams] = useSearchParams();
  const filter = params.get("f") ?? "all";
  const list = useApi<{ items: SetRow[] }>(`/change-sets?filter=${filter}`);
  const selectedId = params.get("set") ?? list.data?.items[0]?.id ?? null;
  const detail = useApi<Detail>(selectedId ? `/change-sets/${selectedId}` : null);
  useEvents(["changeset.updated"], () => {
    list.reload();
    detail.reload();
  });
  const select = (id: string) =>
    setParams((p) => {
      p.set("set", id);
      return p;
    });

  return (
    <div
      className="grid fill-split"
      style={{ gridTemplateColumns: "minmax(0,380px) minmax(0,1fr)" }}
      id="chGrid"
    >
      <div className="card">
        <div className="card-h">
          <div className="seg" role="group" aria-label="Filter">
            {[
              ["all", "All"],
              ["open", "Open"],
              ["applied", "Applied"],
              ["closed", "Closed"],
            ].map(([k, l]) => (
              <button
                type="button"
                key={k}
                className={filter === k ? "on" : ""}
                aria-pressed={filter === k}
                onClick={() =>
                  setParams((p) => {
                    p.set("f", k!);
                    p.delete("set");
                    return p;
                  })
                }
              >
                {l}
              </button>
            ))}
          </div>
        </div>
        <div className="card-b flush" id="csList">
          {list.error ? <ErrorBox error={list.error} retry={list.reload} /> : null}
          {list.loading && !list.data ? <Loading /> : null}
          {list.data?.items.length === 0 ? (
            <Empty
              action={
                <Link className="btn sm" to="/campaigns">
                  Open Campaigns
                </Link>
              }
            >
              No change sets yet. Edit a budget or status in Campaigns, or negate search terms, and your
              drafts collect here.
            </Empty>
          ) : null}
          {list.data?.items.map((s) => (
            <button
              type="button"
              key={s.id}
              className={`cs${s.id === selectedId ? " on" : ""}`}
              onClick={() => select(s.id)}
              style={{ width: "100%", textAlign: "left" }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="t">{s.title}</div>
                <div className="s">
                  {s.ops ?? 0} ops · {s.source_kind}
                  {s.simulated ? " · simulated" : ""}
                </div>
              </div>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
                <StatePill status={s.status} />
                <span className="muted mono" style={{ fontSize: 11 }}>
                  {when(s.created_at)}
                </span>
              </div>
            </button>
          ))}
        </div>
      </div>
      <div className="card" id="csDetail" style={{ display: "flex", flexDirection: "column" }}>
        {detail.error ? <ErrorBox error={detail.error} retry={detail.reload} /> : null}
        {detail.data ? (
          <SetDetail
            d={detail.data}
            onChanged={() => {
              list.reload();
              detail.reload();
              window.dispatchEvent(new Event("drafts:changed"));
            }}
            onSelect={select}
          />
        ) : !detail.loading ? (
          <Empty>Select a change set.</Empty>
        ) : (
          <Loading />
        )}
      </div>
    </div>
  );
}

function SetDetail({
  d,
  onChanged,
  onSelect,
}: {
  d: Detail;
  onChanged: () => void;
  onSelect: (id: string) => void;
}) {
  const { can, me } = useSession();
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ tone: string; text: string } | null>(null);
  const s = d.set;
  const editable = ["draft", "invalid"].includes(s.status) && can("analyst");

  const act = async (path: string, done?: (r: Record<string, unknown>) => string) => {
    setBusy(true);
    setMsg(null);
    try {
      const r = await api.post<Record<string, unknown>>(`/change-sets/${s.id}/${path}`);
      setMsg({ tone: "ok", text: done ? done(r ?? {}) : "Done." });
      if (path === "revert" && typeof r?.id === "string") onSelect(r.id);
    } catch (e) {
      setMsg({ tone: "err", text: (e as Error).message });
    } finally {
      setBusy(false);
      onChanged();
    }
  };
  const removeOp = async (opId: string) => {
    await api.del(`/change-sets/${s.id}/ops/${opId}`);
    onChanged();
  };

  // campaigns created in this set have temporary ids (-2…): group their ops, budget included,
  // under the new campaign's name
  const created = new Map(
    d.ops
      .filter((x) => x.resource_type === "campaign" && x.action === "create" && x.entity.tempId)
      .map((x) => [x.entity.tempId!, x]),
  );
  const budgetOf = new Map([...created.values()].map((x) => [x.entity.budgetId, x.entity.tempId!]));
  const groups = new Map<string, OpRow[]>();
  for (const o of d.ops) {
    const cid =
      o.entity.campaignId ??
      (o.resource_type === "campaign" ? o.entity.tempId : undefined) ??
      (o.resource_type === "campaign_budget" ? budgetOf.get(o.entity.tempId) : undefined);
    const fresh = cid ? created.get(cid) : undefined;
    const name = !cid
      ? "Account"
      : fresh
        ? `New campaign · ${String(fresh.fields.name ?? "")}`
        : (d.campaignNames[cid] ?? `Campaign ${cid}`);
    groups.set(name, [...(groups.get(name) ?? []), o]);
  }
  const verdicts = { allow: 0, ask: 0, block: 0 } as Record<string, number>;
  for (const o of d.ops)
    verdicts[o.guardrail?.verdict ?? "allow"] = (verdicts[o.guardrail?.verdict ?? "allow"] ?? 0) + 1;

  return (
    <>
      <div className="card-h" style={{ paddingBottom: 10, borderBottom: "1px solid var(--border)" }}>
        <div>
          <h2>{s.title}</h2>
          <div className="muted" style={{ fontSize: 12 }}>
            {s.source_kind}
            {s.created_by ? ` · by ${d.people[s.created_by] ?? "someone"}` : ""}
            {s.approved_by ? ` · decided by ${d.people[s.approved_by] ?? "someone"}` : ""}
            {s.apply_at && s.status === "scheduled" ? ` · applies ${when(s.apply_at)}` : ""}
            {s.reverts_id ? (
              <>
                {" "}
                · reverts{" "}
                <button type="button" className="linkish" onClick={() => onSelect(s.reverts_id!)}>
                  an earlier set
                </button>
              </>
            ) : null}
          </div>
        </div>
        <StatePill status={s.status} />
      </div>
      <div className="card-b stack">
        {msg ? (
          <div className={`flash ${msg.tone}`} role="status">
            {msg.text}
          </div>
        ) : null}
        {s.simulated ? (
          <div className="flash info">
            Simulated in Safe mode: validated only, nothing was written to Google Ads.
          </div>
        ) : null}
        {d.ops.length === 0 ? <Empty>This change set has no ops.</Empty> : null}
        {[...groups].map(([name, ops]) => (
          <div className="opgroup" key={name}>
            <div className="gh">
              <Icon name="layers" size={14} />
              {name}
              <span className="muted" style={{ fontWeight: 500 }}>
                {ops.length} op{ops.length === 1 ? "" : "s"}
              </span>
            </div>
            {ops.map((o) => {
              const v = o.guardrail?.verdict ?? "allow";
              const keys = o.update_mask?.length ? o.update_mask : Object.keys(o.before ?? {});
              return (
                <div key={o.id}>
                  <div className="op">
                    <span className={v === "allow" ? "pos" : v === "block" ? "neg" : "t2"} title={v}>
                      <Icon name={v === "allow" ? "check" : v === "ask" ? "alert" : "x"} size={15} />
                    </span>
                    <div>
                      <div>{o.label}</div>
                      <div className="res">
                        {o.resource_type}.{o.action}
                        {o.reason ? ` · ${o.reason}` : ""}
                      </div>
                    </div>
                    <div className="v hide-s">
                      <s className="muted">
                        {o.action === "create" ? "—" : keys.map((f) => fmt(f, o.before?.[f])).join(" ")}
                      </s>
                    </div>
                    <div className="v">
                      {o.action === "remove"
                        ? "removed"
                        : Object.entries(o.fields)
                            .filter(([f]) => f !== "negative")
                            .map(([f, val]) => fmt(f, val))
                            .join(" ")}
                    </div>
                    <div style={{ textAlign: "right" }}>
                      {o.status === "applied" ? (
                        <Chip tone="good">{o.after?.simulated ? "simulated" : "applied"}</Chip>
                      ) : ["failed", "conflict", "invalid", "skipped"].includes(o.status) ? (
                        <Chip tone="crit" title={o.google_error?.message}>
                          {o.status}
                        </Chip>
                      ) : editable ? (
                        <button type="button" className="btn sm ghost" onClick={() => removeOp(o.id)}>
                          Remove
                        </button>
                      ) : null}
                    </div>
                  </div>
                  {o.guardrail?.checks.some((c) => c.key !== "default") || o.google_error ? (
                    <div className="checks" style={{ padding: "4px 12px 10px 44px" }}>
                      {o.guardrail?.checks
                        .filter((c) => c.key !== "default")
                        .map((c) => (
                          <div
                            key={c.key}
                            className={c.verdict === "allow" ? "ok" : c.verdict === "ask" ? "ask" : "no"}
                          >
                            {c.reason}
                          </div>
                        ))}
                      {o.google_error ? <div className="no">{o.google_error.message}</div> : null}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        ))}
        <div className="muted" style={{ fontSize: 12 }}>
          Guardrails: {verdicts.allow ?? 0} ok · {verdicts.ask ?? 0} need approval · {verdicts.block ?? 0}{" "}
          blocked
        </div>
      </div>
      <div className="det-foot">
        {editable ? (
          <>
            <button
              type="button"
              className="btn"
              disabled={busy}
              onClick={() =>
                act("validate", (r) => {
                  const c = r.verdicts as Record<string, number>;
                  return `Guardrails: ${c.allow} ok, ${c.ask} need approval, ${c.block} blocked.`;
                })
              }
            >
              Validate
            </button>
            <button
              type="button"
              className="btn pri"
              disabled={busy || !d.ops.length}
              onClick={() =>
                act("submit", (r) =>
                  r.status === "invalid"
                    ? "Some changes are blocked by guardrails. Remove or fix them."
                    : "Submitted. A manager can approve it in Suggestions.",
                )
              }
            >
              Submit for approval
            </button>
            <button
              type="button"
              className="btn ghost danger"
              disabled={busy}
              onClick={() => act("discard", () => "Draft discarded.")}
            >
              Discard
            </button>
          </>
        ) : null}
        {s.status === "pending_approval" && can("manager") ? (
          <>
            <button
              type="button"
              className="btn pri"
              disabled={busy}
              onClick={() =>
                act("approve", () =>
                  me.workspace.safeMode
                    ? "Approved. Safe mode is on: it will be validated with Google, not applied."
                    : "Approved. Applying now.",
                )
              }
            >
              Approve &amp; apply
            </button>
            <button
              type="button"
              className="btn"
              disabled={busy}
              onClick={() => act("reject", () => "Rejected.")}
            >
              Reject
            </button>
          </>
        ) : null}
        {s.status === "scheduled" && can("manager") ? (
          <>
            <button
              type="button"
              className="btn pri"
              disabled={busy}
              onClick={() => act("approve", () => "Applying now.")}
            >
              Apply now
            </button>
            <button
              type="button"
              className="btn"
              disabled={busy}
              onClick={() => act("reject", () => "Cancelled.")}
            >
              Cancel
            </button>
          </>
        ) : null}
        {["applied", "partially_applied"].includes(s.status) && can("manager") ? (
          <button
            type="button"
            className="btn"
            disabled={busy}
            onClick={() => act("revert", () => "Revert drafted as a new change set; approve it to undo.")}
          >
            Revert
          </button>
        ) : null}
        {s.status === "pending_approval" && !can("manager") ? (
          <span className="muted">Waiting for a manager to approve.</span>
        ) : null}
        <span className="sp" />
        {me.workspace.safeMode ? (
          <span className="muted" style={{ fontSize: 12 }}>
            <Icon name="shield" size={13} /> Safe mode: approving only validates.
          </span>
        ) : null}
      </div>
    </>
  );
}

function AuditLog() {
  const { data, error, loading, reload } = useApi<{ items: AuditRow[] }>("/audit-log");
  if (error) return <ErrorBox error={error} retry={reload} />;
  if (loading && !data) return <Loading />;
  if (!data?.items.length)
    return (
      <div className="card">
        <Empty>
          Nothing has been applied yet. Applied and simulated changes land here with before and after values.
        </Empty>
      </div>
    );
  return (
    <div className="card fill-card">
      <div className="card-b flush">
        <div className="tbl-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>When</th>
                <th>Change</th>
                <th>Source</th>
                <th>Before</th>
                <th>After</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {data.items.map((a) => (
                <tr key={a.id}>
                  <td className="mono">{when(a.at)}</td>
                  <td>
                    <span className="ttl">{a.label}</span>
                  </td>
                  <td>{a.actor_kind}</td>
                  <td className="mono">
                    {Object.entries(a.before ?? {})
                      .map(([k, v]) => fmt(k, v))
                      .join(", ")}
                  </td>
                  <td className="mono">
                    {Object.entries(a.after ?? {})
                      .filter(([k]) => k !== "negative")
                      .map(([k, v]) => fmt(k, v))
                      .join(", ")}
                  </td>
                  <td>{a.simulated ? <Chip tone="acc">simulated</Chip> : null}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
